import {
  BUCKET_THRESHOLDS,
  IMPACT_WEIGHT,
  type ScoreCriterion,
  type ScoreEntity,
  whereViewIds,
} from '@parcel/filters';
import { Badge, Button, HoverCard, HoverCardContent, HoverCardTrigger, cn } from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import {
  DATASET_BY_ENTITY,
  type Score,
  type ScoreValue,
  bucketIndex,
  bucketLabel,
  useScore,
  valueBucketIndex,
} from '../../lib/scores.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import { useWorkspaceViews } from '../../lib/workspace-views.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { criterionChips, criterionTopic } from './criteria-state.js';

/** Brand palette, top bucket down: green, amber, crimson, soft crimson; each resolves by theme. */
const COLORS = [
  'var(--tone-green-dark-dot)',
  'var(--tone-amber-dot)',
  'var(--accent-crimson-solid)',
  'var(--accent-crimson-soft)',
] as const;

const clampBucket = (index: number) => Math.min(Math.max(index, 0), 3);

/** The meter and dot color for a bucket position. */
export function bucketColor(index: number): string {
  return COLORS[clampBucket(index)] as string;
}

/** Filled dashes out of five: the bucket thresholds step them, and a full meter waits for 90. */
export function scoreDashes(score: number): number {
  const [top, middle, low] = BUCKET_THRESHOLDS;
  if (score >= 90) return 5;
  if (score >= top) return 4;
  if (score >= middle) return 3;
  if (score >= low) return 2;
  return score > 0 ? 1 : 0;
}

/** Five dashes in the bucket's color and the number; hover for what made it. */
export function ScoreBar({
  entity,
  value,
  score,
  side = 'right',
}: {
  entity: ScoreEntity;
  value: ScoreValue | null | undefined;
  score: Score | undefined;
  /** Which side of the score the card opens on; it flips only at the window's edge. */
  side?: 'left' | 'right';
}) {
  // A hover card ignores the keyboard, so Enter or Space pins this one open and moves focus into it.
  const [hovered, setHovered] = React.useState(false);
  const [pinned, setPinned] = React.useState(false);
  const trigger = React.useRef<HTMLButtonElement>(null);
  const content = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!pinned) return;
    const frame = requestAnimationFrame(() =>
      content.current?.querySelector<HTMLElement>('a[href], button')?.focus(),
    );
    return () => cancelAnimationFrame(frame);
  }, [pinned]);
  const unpin = () => {
    setPinned(false);
    setHovered(false);
  };
  return (
    <HoverCard open={pinned || hovered} onOpenChange={setHovered} openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          ref={trigger}
          type="button"
          className="relative z-10 inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={value ? `${score?.name ?? 'Score'} ${value.score} of 100` : 'Not scored'}
          aria-expanded={pinned || hovered}
          data-testid="score-bar"
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            setPinned(true);
          }}
        >
          <ScoreDashes value={value} score={score} />
        </button>
      </HoverCardTrigger>
      {/* Beside the score, never above or below it, so it does not jump between rows. */}
      <HoverCardContent
        ref={content}
        side={side}
        align="start"
        sideOffset={8}
        collisionPadding={12}
        className="flex max-h-[min(28rem,var(--radix-hover-card-content-available-height))] w-80 flex-col p-0"
        // React bubbles portal clicks to the row, which would open the record's drawer too.
        onClick={(event) => event.stopPropagation()}
        onEscapeKeyDown={() => {
          unpin();
          trigger.current?.focus();
        }}
        onPointerDownOutside={unpin}
        onFocusOutside={(event) => {
          if (event.target !== trigger.current) unpin();
        }}
      >
        <ScoreCard entity={entity} value={value} score={score} />
      </HoverCardContent>
    </HoverCard>
  );
}

/** The dashes and the number, without the hover card. */
export function ScoreDashes({
  value,
  score,
}: {
  value: ScoreValue | null | undefined;
  score: Pick<Score, 'buckets'> | undefined;
}) {
  const filled = value ? scoreDashes(value.score) : 0;
  const color = value ? bucketColor(valueBucketIndex(score, value)) : undefined;
  return (
    <span className="inline-flex items-center gap-2 tabular-nums">
      <span className="flex items-center gap-[3px]" aria-hidden>
        {[0, 1, 2, 3, 4].map((dash) => (
          <span
            key={dash}
            className={cn('h-1 w-3.5 rounded-full', dash < filled ? null : 'bg-muted')}
            style={dash < filled ? { backgroundColor: color } : undefined}
          />
        ))}
      </span>
      <span
        className={cn(
          'w-[3ch] shrink-0 text-right text-sm',
          value ? null : 'text-muted-foreground',
        )}
      >
        {value ? value.score : '-'}
      </span>
    </span>
  );
}

const CHIP_LIMIT = 4;

type RowTone = 'match' | 'miss' | 'gate';

/** One row: a short name and the values asked for under it, "Market  Boston  Cambridge". */
function CriterionLine({
  topic,
  chips,
  note,
  tone,
}: {
  topic: string;
  chips: string[];
  note?: string;
  tone: RowTone;
}) {
  const shown = chips.slice(0, CHIP_LIMIT);
  return (
    <li className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-start gap-2">
      <span
        className={cn(
          'pt-0.5 text-xs leading-4',
          tone === 'match' && 'text-foreground',
          tone === 'miss' && 'text-muted-foreground',
          tone === 'gate' && 'text-destructive',
        )}
      >
        {topic}
      </span>
      <div className="flex flex-wrap gap-1">
        {shown.map((chip) => (
          <span
            key={chip}
            className={cn(
              'rounded px-1.5 py-0.5 text-[11px] leading-4',
              // A missed criterion's values are outlined: asked for, not there.
              tone === 'miss'
                ? 'border border-dashed text-muted-foreground'
                : 'bg-muted text-foreground',
            )}
          >
            {chip}
          </span>
        ))}
        {chips.length > shown.length ? (
          <span className="px-1 py-0.5 text-[11px] leading-4 text-muted-foreground">
            +{chips.length - shown.length}
          </span>
        ) : null}
        {note ? (
          <span className="py-0.5 text-[11px] leading-4 text-muted-foreground">{note}</span>
        ) : null}
      </div>
    </li>
  );
}

/** The workspace's own names for what criteria point at: its ICP tier labels and its Views. */
export interface CriterionNames {
  viewName?: (id: string) => string | undefined;
}

/** Criteria with the same name and the same note bundle into one row, as the settings page bundles them into one card. */
export function criterionRows(
  entity: ScoreEntity,
  criteria: readonly ScoreCriterion[],
  names: CriterionNames = {},
): Array<{ topic: string; chips: string[]; note?: string }> {
  const rows = new Map<string, { topic: string; chips: string[]; note?: string }>();
  for (const criterion of criteria) {
    const topic = criterionTopic(entity, criterion);
    // A View reads under its own name, never its id.
    const viewLabels = Object.fromEntries(
      whereViewIds(criterion.where).flatMap((id) => {
        const name = names.viewName?.(id);
        return name ? [[id, name]] : [];
      }),
    );
    const { chips, note } = criterionChips(entity, criterion, { options: { view: viewLabels } });
    const key = `${topic}|${note ?? ''}`;
    const row = rows.get(key) ?? { topic, chips: [], ...(note ? { note } : {}) };
    for (const chip of chips) if (!row.chips.includes(chip)) row.chips.push(chip);
    rows.set(key, row);
  }
  return [...rows.values()];
}

function CriterionGroup({
  label,
  entity,
  criteria,
  names,
  tone,
}: {
  label: string;
  entity: ScoreEntity;
  criteria: ScoreCriterion[];
  names?: CriterionNames;
  tone: RowTone;
}) {
  if (criteria.length === 0) return null;
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-medium text-muted-foreground">{label}</h4>
      <ul className="space-y-2">
        {criterionRows(entity, criteria, names).map((row) => (
          <CriterionLine
            key={`${row.topic}:${row.note ?? ''}`}
            topic={row.topic}
            chips={row.chips}
            note={row.note}
            tone={tone}
          />
        ))}
      </ul>
    </section>
  );
}

/** The hover card: the score out of 100 and its bucket, then each criterion with the values it asks for. */
export function ScoreDetail({
  entity,
  value,
  score,
  names,
  action,
}: {
  entity: ScoreEntity;
  value: ScoreValue | null | undefined;
  score: Score | undefined;
  names?: CriterionNames;
  /** Beside the score, e.g. the hover card's link to the criteria. */
  action?: React.ReactNode;
}) {
  const criteria = score?.criteria ?? [];
  const matched = new Set(value?.matched ?? []);
  // A disqualifier that did not match is the good outcome, so it is never listed as missed; heaviest first.
  const pick = (hit: boolean, disqualify: boolean) =>
    criteria
      .filter((c) => matched.has(c.id) === hit && (c.impact === 'disqualify') === disqualify)
      .sort((a, b) => IMPACT_WEIGHT[b.impact] - IMPACT_WEIGHT[a.impact]);
  const index = value ? valueBucketIndex(score, value) : 0;
  return (
    <div className="flex min-h-0 flex-col" data-testid="score-detail">
      <div className="flex shrink-0 items-center justify-between gap-3 p-4">
        <div className="space-y-1">
          <div className="text-sm font-medium">{score?.name ?? 'Score'}</div>
          {value ? (
            // One text baseline for the number and the bucket, so neither sits higher.
            <div className="flex items-baseline gap-3 text-sm tabular-nums">
              <span>
                <span className="text-base font-semibold">{value.score}</span>
                <span className="text-xs text-muted-foreground"> /100</span>
              </span>
              <span>
                <span
                  className="mr-1.5 inline-block size-2 rounded-full align-[0.05em]"
                  style={{ backgroundColor: bucketColor(index) }}
                  aria-hidden
                />
                {bucketLabel(score, value.bucket)}
              </span>
            </div>
          ) : null}
        </div>
        {action}
      </div>
      {value ? (
        // The only part that scrolls: the score and its link above stay put.
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto border-t px-4 py-3">
          <CriterionGroup
            label="Disqualified"
            entity={entity}
            criteria={pick(true, true)}
            names={names}
            tone="gate"
          />
          <CriterionGroup
            label="Matches"
            entity={entity}
            criteria={pick(true, false)}
            names={names}
            tone="match"
          />
          <CriterionGroup
            label="Missing"
            entity={entity}
            criteria={pick(false, false)}
            names={names}
            tone="miss"
          />
        </div>
      ) : (
        <p className="border-t px-4 py-3 text-xs text-muted-foreground">
          {criteria.length === 0
            ? 'Not scored yet. Set up the criteria in Settings, Scoring.'
            : 'Not scored yet. Scores refresh every six hours and after each save.'}
        </p>
      )}
    </div>
  );
}

/** The open card, with the workspace's own names: read only once it opens, from caches the pages already fill. */
function ScoreCard({
  entity,
  value,
  score,
}: {
  entity: ScoreEntity;
  value: ScoreValue | null | undefined;
  score: Score | undefined;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const slug = getWorkspaceSlugFromPath(pathname);
  const workspaceId = useQuery({
    queryKey: ['workspace', slug],
    enabled: Boolean(slug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(slug ?? ''),
  }).data?.id;
  const criteria = score?.criteria ?? [];
  const own = useWorkspaceViews(
    workspaceId,
    entity,
    criteria.some((c) => whereViewIds(c.where).length > 0),
  ).data;
  const names: CriterionNames = {
    viewName: (id) => own?.find((view) => view.id === id)?.name,
  };
  return (
    <ScoreDetail
      entity={entity}
      value={value}
      score={score}
      names={names}
      action={<ViewCriteriaLink entity={entity} />}
    />
  );
}

/** The hover card's way to the score's settings page. */
function ViewCriteriaLink({ entity }: { entity: ScoreEntity }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const slug = getWorkspaceSlugFromPath(pathname);
  if (!slug) return null;
  return (
    <Button asChild variant="outline" size="sm" className="h-7 shrink-0 px-2.5 text-xs">
      <Link
        to="/$workspaceSlug/settings/scoring/$entity"
        params={{ workspaceSlug: slug, entity: DATASET_BY_ENTITY[entity] }}
      >
        View criteria
      </Link>
    </Button>
  );
}

export function BucketBadge({
  value,
  score,
}: {
  value: ScoreValue | null | undefined;
  score: Pick<Score, 'buckets'> | undefined;
}) {
  if (!value) return <span className="text-muted-foreground">-</span>;
  const index = bucketIndex(score, value.bucket);
  if (index < 2) {
    return (
      <Badge tone={index === 0 ? 'green-dark' : 'amber'} dot className="whitespace-nowrap">
        {bucketLabel(score, value.bucket)}
      </Badge>
    );
  }
  return (
    <Badge variant="red" className="whitespace-nowrap">
      <span className="size-1.5 rounded-full" style={{ backgroundColor: bucketColor(index) }} />
      {bucketLabel(score, value.bucket)}
    </Badge>
  );
}
